import InputBase, { type InputBaseComponentProps } from '@mui/material/InputBase'
import TextField, { type StandardTextFieldProps } from '@mui/material/TextField'
import React from 'react'
import { Controller, type FieldValues, type Path, type UseFormReturn, get } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'

import ErrorField from './ErrorField'

type NumberFormatProps = Omit<InputBaseComponentProps, 'defaultValue'>

const NumberFormat: React.FC<NumberFormatProps> = ({ onChange, value, ...rest }) => (
  <NumericFormat
    customInput={InputBase}
    decimalScale={2}
    decimalSeparator=','
    fixedDecimalScale
    onValueChange={onChange == null ? undefined : (ev) => onChange({ target: { value: ev.floatValue == null ? '' : (ev.floatValue * 100).toFixed(0) } })}
    style={{ paddingBottom: 15 }}
    suffix=' kr'
    thousandSeparator=' '
    value={value == null ? undefined : Number.parseInt(value, 10) / 100}
    {...rest}
  />
)

interface MoneyFieldBaseProps {
  onChange?: (value: number | undefined) => void
  value?: number | null
}

interface UncontrolledMoneyFieldProps<TFieldValues extends FieldValues> {
  form: Pick<UseFormReturn<TFieldValues>, 'control' | 'formState'>
  label?: string
  name: Path<TFieldValues>
  required?: boolean
}

type ControlledMoneyFieldProps = Omit<StandardTextFieldProps, 'onChange' | 'type' | 'value'> & MoneyFieldBaseProps

export default function MoneyField<T extends FieldValues> (props: UncontrolledMoneyFieldProps<T> | ControlledMoneyFieldProps): JSX.Element {
  if ('form' in props) {
    const error = get(props.form.formState.errors, props.name)

    return (
      <>
        <Controller
          control={props.form.control}
          name={props.name}
          render={({ field: { onBlur, onChange, value } }) => (
            <MoneyField
              error={error != null}
              id={props.name}
              label={props.label}
              onBlur={onBlur}
              onChange={onChange}
              required={props.required}
              value={value}
            />
          )}
        />

        <ErrorField message={error?.message} />
      </>
    )
  }

  const { onChange, value, ...rest } = props

  return (
    <TextField
      InputProps={{ inputComponent: NumberFormat }}
      onChange={onChange == null ? undefined : (ev) => onChange(ev.target.value !== '' ? Number.parseInt(ev.target.value, 10) : undefined)}
      value={value == null ? undefined : String(value)}
      {...rest}
    />
  )
}
